/**
 * Capability Overview - Horizontal Accordion Module
 *
 * Handles horizontal accordion interaction on desktop and
 * ensures stacked layout stability on mobile.
 */

export const initCapabilityOverview = () => {
	const blocks = document.querySelectorAll('.capability-overview');
	if (!blocks.length) return;

	blocks.forEach((block) => {
		if (block.dataset.coInitialized === 'true') return;
		block.dataset.coInitialized = 'true';

		const items = Array.from(block.querySelectorAll('.co-item'));
		if (!items.length) return;

		const isMobileView = () => window.innerWidth < 992;

		let currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
		if (currentIndex === -1) {
			currentIndex = 0;
		}

		let timer = null;
		let isPaused = false;
		let isIntersecting = false;

		const activateItem = (targetIndex, shouldFocus = false) => {
			// On mobile, items are displayed stacked; accordion tab switching is desktop-only
			if (isMobileView()) return;

			items.forEach((item, index) => {
				const button = item.querySelector('.co-tab-btn');
				const video = item.querySelector('video');
				const isTarget = index === targetIndex;

				if (isTarget) {
					item.classList.add('is-active');
					if (button) {
						button.setAttribute('aria-expanded', 'true');
						button.setAttribute('aria-selected', 'true');
						button.setAttribute('tabindex', '0');
						if (shouldFocus) {
							button.focus();
						}
					}
					if (video) {
						video.play().catch(() => {});
					}
				} else {
					item.classList.remove('is-active');
					if (button) {
						button.setAttribute('aria-expanded', 'false');
						button.setAttribute('aria-selected', 'false');
						button.setAttribute('tabindex', '-1');
					}
					if (video) {
						video.pause();
					}
				}
			});
		};

		const startAutoPlay = () => {
			stopAutoPlay();
			if (isMobileView() || items.length <= 1) return;

			timer = setInterval(() => {
				if (isPaused || isMobileView()) return;
				currentIndex = (currentIndex + 1) % items.length;
				activateItem(currentIndex, false);
			}, 2500);
		};

		const stopAutoPlay = () => {
			if (timer) {
				clearInterval(timer);
				timer = null;
			}
		};

		// Pause on hover over the accordion wrap
		const accordionWrap = block.querySelector('.co-accordion-wrap');
		if (accordionWrap) {
			accordionWrap.addEventListener('mouseenter', () => {
				isPaused = true;
			});

			accordionWrap.addEventListener('mouseleave', () => {
				isPaused = false;
			});
		}

		items.forEach((item, index) => {
			const button = item.querySelector('.co-tab-btn');
			if (!button) return;

			// Click handler
			button.addEventListener('click', (e) => {
				e.preventDefault();
				currentIndex = index;
				activateItem(index, false);
				if (isIntersecting && !isPaused) {
					startAutoPlay();
				}
			});

			// Keyboard accessibility for tablist on desktop
			button.addEventListener('keydown', (e) => {
				if (isMobileView()) return;

				let newIndex = null;

				switch (e.key) {
					case 'ArrowRight':
					case 'ArrowDown':
						e.preventDefault();
						newIndex = (index + 1) % items.length;
						break;
					case 'ArrowLeft':
					case 'ArrowUp':
						e.preventDefault();
						newIndex = (index - 1 + items.length) % items.length;
						break;
					case 'Home':
						e.preventDefault();
						newIndex = 0;
						break;
					case 'End':
						e.preventDefault();
						newIndex = items.length - 1;
						break;
					case 'Enter':
					case ' ':
						e.preventDefault();
						currentIndex = index;
						activateItem(index, false);
						if (isIntersecting && !isPaused) {
							startAutoPlay();
						}
						break;
					default:
						break;
				}

				if (newIndex !== null) {
					currentIndex = newIndex;
					activateItem(newIndex, true);
					if (isIntersecting && !isPaused) {
						startAutoPlay();
					}
				}
			});
		});

		// Ensure active panel videos play on init
		const initialActive = block.querySelector('.co-item.is-active video');
		if (initialActive) {
			initialActive.play().catch(() => {});
		}

		// Viewport detection via IntersectionObserver
		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						isIntersecting = entry.isIntersecting;
						if (entry.isIntersecting) {
							startAutoPlay();
						} else {
							stopAutoPlay();
						}
					});
				},
				{
					threshold: 0.15,
				}
			);

			observer.observe(block);
		} else {
			isIntersecting = true;
			startAutoPlay();
		}

		// Handle resize and tab visibility
		window.addEventListener('resize', () => {
			if (isMobileView()) {
				stopAutoPlay();
			} else if (isIntersecting && !timer) {
				startAutoPlay();
			}
		});

		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				stopAutoPlay();
			} else if (isIntersecting && !isMobileView()) {
				startAutoPlay();
			}
		});
	});
};

// Self-initialize on DOMContentLoaded or immediately if DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCapabilityOverview);
} else {
	initCapabilityOverview();
}

// ACF Block Preview support in Gutenberg editor
if (typeof window !== 'undefined' && window.acf) {
	window.acf.addAction(
		'render_block_preview/type=capability-overview',
		initCapabilityOverview
	);
}

export default initCapabilityOverview;
